import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { usePeriod } from '@/lib/period';
import { PeriodControl } from '@/features/shared/PeriodControl';
import { BackfillButton } from '@/features/shared/BackfillButton';
import { OpenInPostHogButton } from '@/components/OpenInPostHogButton';
import { posthogFunnelUrl } from '@/lib/posthogLinks';
import { ANDROID_CONVERSION_STEPS, WEB_CONVERSION_STEPS } from '@etiquetachef/types';
import { FunnelChart } from './FunnelChart';
import { useFunnel } from './useFunnel';

/**
 * Tela `/dashboard/funnel`: funil de conversão por pessoa (janela de 30 dias
 * terminando no último dia do período), separado por superfície.
 *
 * Origem: sem issue — commit 2c50700f6, 2026-05-04 (Sprint 4.10, ver
 * `docs/sprint-4.10-cohorts-funnel-churn.md`).
 *
 * Contrato: sem props; período pelo `usePeriod`. Estados
 * LOADING/ERROR/EMPTY/READY vêm de `useFunnel`; em READY um aviso diz qual
 * janela e qual dia estão na tela. Ações no cabeçalho: abrir no PostHog e
 * recalcular o período (`BackfillButton type="funnel"`, a única escrita).
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/funnel`, só super-admin).
 *
 * Arquitetura: só apresentação; leitura no `useFunnel`, reprocessamento pela
 * callable `backfillAggregations` (via `BackfillButton`).
 *
 * Falhas conhecidas:
 *  - até 2026-08-31 (d555e95f8): as barras eram clicáveis e o drilldown
 *    (`listDrilldown`) não implementa `funnelStep` — todo clique dava erro.
 *    As barras deixaram de ser botão; não religar sem implementar a métrica.
 *
 * Issues: #68 (deep-link PostHog e recalcular), #69 (funil por pessoa).
 */
export function FunnelDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { period, setPeriod } = usePeriod();
  const state = useFunnel(period);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('funnel.subtitle')}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <OpenInPostHogButton
                href={posthogFunnelUrl([
                  // As etapas comuns do funil atual (`COMMON_STEPS`).
                  // `account_created`/`onboarding_started` saíram com o
                  // onboarding e abriam o PostHog num funil zerado (#939).
                  'paywall_viewed',
                  'subscription_paid',
                  'product_created',
                  'label_created',
                  'label_printed_lite',
                ])}
                surface="funnel"
              />
              <BackfillButton type="funnel" />
            </SpaceBetween>
          }
        >
          {t('funnel.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <PeriodControl value={period} onChange={setPeriod} />

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'EMPTY' && (
          <Alert type="info">{t('funnel.empty')}</Alert>
        )}
        {state.kind === 'READY' && (
          <>
            {/* Cada doc é o funil de uma janela móvel, não do período inteiro:
                dizer em voz alta qual janela está na tela evita ler o número
                como se fosse do intervalo escolhido no seletor. */}
            <Alert type="info">
              {t('funnel.windowNotice', {
                days: state.day.windowDays,
                date: state.day.yyyymmdd,
                conversionDays: state.day.conversionWindowDays,
              })}
            </Alert>
            <FunnelChart
              title={t('funnel.chart.androidTitle')}
              order={ANDROID_CONVERSION_STEPS}
              steps={state.day.bySurface.android}
            />
            <FunnelChart
              title={t('funnel.chart.webTitle')}
              order={WEB_CONVERSION_STEPS}
              steps={state.day.bySurface.web}
            />
          </>
        )}
      </SpaceBetween>

    </ContentLayout>
  );
}
